<script setup lang="ts">
import { BetPop } from '@/components/BetPop'
import { useRouteCacheStore } from '@/stores'
import TabBar from "@/components/TabBar.vue"
import AppToast from '@/components/AppToast.vue'
import InstallPWA from '@/components/InstallPwa/index.vue'
import AuthDialog from '@/components/AuthDialog.vue'
// 移除 GlobalPopup 导入
// import GlobalPopup from '@/components/GlobalPopup.vue'
import { useRoute } from 'vue-router'
import { getGlobalSocket } from '@/socket'
import { useUserStore } from '@/stores/modules/user'
import { checkAllActivityBadges } from '@/utils/activity-badge'
import { onMounted, ref, watch, onUnmounted } from 'vue'
import { useMessageStore } from '@/stores/modules/message'
import { useFirebasePushInApp } from '@/utils/firebase-push-service'
// 导入 visitApp 接口
import { visitApp } from '@/api/game'
// 导入 signIn 接口
import { signIn } from '@/api/game'
// 添加 SocketStatus 导入
import { SocketStatus } from '@/socket/config'
import { initializeAnalyticsApp } from '@/utils/firebase-analytics'
import { checkAndTrackFirstOpen, checkAndTrackPwaFirstOpen } from '@/utils/firebase-analytics';
import { useAppStore } from '@/stores/index'
import { initFacebookPixel } from '@/utils/facebook-pixel-tracking'
const appStore = useAppStore()
const {
  initializeFirebasePush,
  destroyService
} = useFirebasePushInApp();

// 只在App.vue初始化一次socket
const { socket } = getGlobalSocket()
window.$socket = socket

const routeCacheStore = useRouteCacheStore()
const route = useRoute()
const userStore = useUserStore()
const messageStore = useMessageStore()

// 移除弹窗相关状态和处理函数
// const showPopup = ref(false)
// const handlePopupClose = (noReminderToday, popupData) => { ... }
// const handleAllPopupsClosed = (noReminderToday) => { ... }
// const handleImageClick = (popupData) => { ... }

// 获取 channelId 的函数
const getChannelId = () => {
  // 直接从当前 URL 参数获取 channelId
  let urlChannelId = route.query.channelId as any
  if (urlChannelId) {
    return urlChannelId
  } else {
    return urlChannelId = 1
  }
}
console.log(window.__BUILD_TIMESTAMP__, "__BUILD_TIMESTAMP__")
// 检查是否已经过了5分钟
const canSignIn = () => {
  const lastSignInTime = localStorage.getItem('signIn_lastCallTime')
  if (!lastSignInTime) {
    return true // 第一次签到
  }

  const now = Date.now()
  const lastTime = parseInt(lastSignInTime)
  const fiveMinutes = 5 * 60 * 1000 // 5分钟的毫秒数

  return (now - lastTime) >= fiveMinutes
}

// 记录签到时间
const markSignInTime = () => {
  const now = Date.now()
  localStorage.setItem('signIn_lastCallTime', now.toString())
}

// 调用签到接口
const callSignIn = async () => {
  try {
    // 检查用户是否已登录
    if (!userStore.isLoggedIn) {
      console.log('用户未登录，跳过签到')
      return
    }

    // 检查是否已经过了5分钟
    if (!canSignIn()) {
      console.log('距离上次签到未满5分钟，跳过')
      return
    }

    console.log('调用签到接口')
    await signIn()

    // 记录签到时间
    markSignInTime()
    console.log('签到接口调用成功，已记录签到时间')
  } catch (error) {
    console.error('签到接口调用失败:', error)
  }
}

// 调用 visitApp 接口
const callVisitApp = async () => {
  try {
    const channelId = getChannelId()
    visitApp({ channelId })
  } catch (error) {
    console.error('visitApp 接口调用失败:', error)
  }
}

// 初始化 Socket 连接
const initSocket = () => {
  const socket = window.$socket
  if (socket) {
    // 确保 Socket 已连接
    if (!socket.isConnected()) {
      socket.reconnect()
    }

    // 先移除之前的事件监听器，避免重复注册
    socket.off('red_point_notice')

    // 监听消息事件
    socket.on('red_point_notice', (data) => {
      const count = data?.redPointMap?.[1] || 0
      messageStore.setCount(count)
    })
    if (socket.isConnected()) {
      appStore.updateSocketConnected(true)
      console.log(appStore.isSocketConnected)
    }
    console.log('✅ Socket 连接已初始化，连接状态:', socket.isConnected(), '认证状态:', socket.isAuthenticated())
  }
}

const keepAliveRouteNames = computed(() => {
  return routeCacheStore.routeCaches
})

const mode = computed(() => {
  return isDark.value ? 'dark' : 'light'
})

const appToast = ref(null)

// 签到定时器
let signInTimer: NodeJS.Timeout | null = null

// 启动签到定时器
const startSignInTimer = () => {
  // 清除之前的定时器
  if (signInTimer) {
    clearInterval(signInTimer)
  }

  // 立即执行一次签到
  callSignIn()

  // 设置每5分钟执行一次签到
  signInTimer = setInterval(() => {
    callSignIn()
  }, 5 * 60 * 1000) // 5分钟 = 5 * 60 * 1000 毫秒

  console.log('✅ 签到定时器已启动，每5分钟执行一次')
}

// 停止签到定时器
const stopSignInTimer = () => {
  if (signInTimer) {
    clearInterval(signInTimer)
    signInTimer = null
    console.log('⏹️ 签到定时器已停止')
  }
}

// 你所有需要显示TabBar的一级页面路径
const tabPaths = ['/', '/activity', '/Deposito', '/wallet', '/person']

// 判断当前路由是否属于一级页面
const showTabBar = computed(() => {
  // 如果当前是个人中心页面，总是显示TabBar
  if (route.path === '/person') {
    return true
  }

  // 检查是否是从个人中心跳转
  const isFromPerson = sessionStorage.getItem('fromPerson') === 'true'

  // 检查是否是从首页跳转
  const isFromHome = sessionStorage.getItem('fromHome') === 'true'

  // 如果是从个人中心或首页跳转，隐藏TabBar
  if (isFromPerson || isFromHome) {
    return false
  }

  // 如果是从底部tab直接进入充值页面，显示TabBar
  if (route.path === '/Deposito') {
    return true
  }

  return tabPaths.includes(route.path)
})


// 马甲包的情况下，进行充值上报
// 监听路由变化，清除来源标记
watch(() => route.path, (newPath, oldPath) => {
  // 如果回到个人中心页面，清除标记
  if (newPath === '/person') {
    sessionStorage.removeItem('fromPerson')
  }

  // 如果回到首页，清除标记
  if (newPath === '/') {
    sessionStorage.removeItem('fromHome')
  }

  // 如果从充值页面离开，清除所有来源标记
  if (oldPath === '/Deposito') {
    sessionStorage.removeItem('fromPerson')
    sessionStorage.removeItem('fromHome')
  }
})

// 监听登录状态变化，重新初始化 Socket
watch(() => userStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
    // 用户登录后，延迟一点时间再初始化 Socket，确保 token 已经设置
    setTimeout(() => {
      initSocket()
      // 启动签到定时器
      startSignInTimer()
    }, 500)
  } else {
    // 用户登出时，停止签到定时器并重置socket状态
    stopSignInTimer()
    const socket = window.$socket
    if (socket) {
      // 重置socket状态，但不断开连接
      socket.status = SocketStatus.CONNECTED
    }
  }
}, { immediate: true })

// 检查登录状态并恢复
onMounted(async () => {
  // 调用 visitApp 接口
  await callVisitApp()

  // 无论用户是否登录，都初始化 Socket 连接
  initSocket()

  const hasLoginStatus = userStore.checkLoginStatus()

  // 如果用户已登录，检查活动红点状态
  if (hasLoginStatus) {
    try {
      await checkAllActivityBadges()
    } catch (error) {
      console.error('检查活动红点失败:', error)
    }
  }


  console.log('✅ 应用启动完成，Socket状态:', {
    connected: window.$socket?.isConnected(),
    authenticated: window.$socket?.isAuthenticated()
  })

  // 初始化 Facebook Pixel
  await initFacebookPixel()

  initializeFirebasePush()
  const analyticsApp = initializeAnalyticsApp()
  if (analyticsApp) {
    checkAndTrackFirstOpen()
    checkAndTrackPwaFirstOpen()
  }
})

onUnmounted(() => {
  // 停止签到定时器
  stopSignInTimer()
  // 销毁服务
  destroyService();
});
const showBetPop = import.meta.env.MODE == 'development' ? true : false;
</script>

<template>
  <van-config-provider :theme="mode">
    <!-- <nav-bar /> -->
    <router-view v-slot="{ Component }">
      <section class="app-wrapper">
        <keep-alive :include="keepAliveRouteNames">
          <component :is="Component" />
        </keep-alive>
      </section>
    </router-view>
    <TabBar v-if="showTabBar" />
    <AppToast ref="appToast" />
    <InstallPWA />
    <!-- 全局认证弹窗 -->
    <AuthDialog />
    <!-- <BetPop v-if="showBetPop" /> -->
  </van-config-provider>
</template>

<style scoped>
.app-wrapper {
  width: 100%;
  position: relative;
  min-height: 100vh !important;
  background: #083A36;
  /* padding-bottom: calc(50px + env(safe-area-inset-bottom)); */
  box-sizing: border-box;
}

.app-wrapper> :last-child::after {
  content: '';
  display: block;
  width: 100%;
  padding-bottom: calc(98px + env(safe-area-inset-bottom));
}
</style>

<style>
.custom-nav-bar {
  --van-nav-bar-background: linear-gradient(0deg, #167E56, #004F4E);
  /* 修改背景色为绿色 */
  --van-nav-bar-icon-color: #fff;
  /* 修改左侧箭头颜色为白色 */
  --van-nav-bar-text-color: #fff;
  /* 修改文字颜色为白色 */
  --van-nav-bar-title-text-color: #fff;
  /* 修改标题颜色为白色 */
}

input {
  background: none;
  border: none;
  outline: none;
}

/* 浏览器自动填充样式覆盖 */
.auth-input:-webkit-autofill,
.auth-input:-webkit-autofill:hover,
.auth-input:-webkit-autofill:focus,
.auth-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px #11412f inset !important;
  -webkit-text-fill-color: #fff !important;
  transition: background-color 5000s ease-in-out 0s;
  background-color: #11412f !important;
}
</style>
